import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Edit2, Trash2, Building2, Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AreasFuncionales = () => {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nombre: '', gerencia: '', descripcion: '', recibe_notificaciones_entregas: false });

  const loadAreas = useCallback(async () => {
    try {
      const resp = await api.get('/areas-funcionales');
      setAreas(resp.data || []);
    } catch (err) { toast.error('Error al cargar áreas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAreas(); }, [loadAreas]);

  const resetForm = () => {
    setForm({ nombre: '', gerencia: '', descripcion: '', recibe_notificaciones_entregas: false });
    setEditId(null);
    setShowForm(false);
  };

  const startEdit = (area) => {
    setForm({
      nombre: area.nombre,
      gerencia: area.gerencia,
      descripcion: area.descripcion || '',
      recibe_notificaciones_entregas: area.recibe_notificaciones_entregas
    });
    setEditId(area.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.gerencia.trim()) {
      toast.error('Nombre y Gerencia son obligatorios');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/areas-funcionales/${editId}`, form);
        toast.success('Área actualizada');
      } else {
        await api.post('/areas-funcionales', form);
        toast.success('Área creada');
      }
      resetForm();
      loadAreas();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al guardar';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/areas-funcionales/${id}`);
      toast.success('Área desactivada');
      loadAreas();
    } catch (err) { toast.error('Error al desactivar'); }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="areas-funcionales-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/usuarios')} data-testid="btn-volver-usuarios">
            <ArrowLeft size={16} className="mr-1" /> Usuarios
          </Button>
          <h1 className="text-xl font-bold text-[#2D2D2D]">Áreas Funcionales</h1>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="btn-nueva-area">
          <Plus size={16} className="mr-1" /> Nueva Área
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="border-2 border-[#9d8272]/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#4E7A79]">
              {editId ? 'Editar Área Funcional' : 'Nueva Área Funcional'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nombre <span className="text-red-500">*</span></Label>
                <Input value={form.nombre} onChange={e => setForm(f => ({...f, nombre: e.target.value}))} placeholder="Ej: Entregas" data-testid="input-area-nombre" />
              </div>
              <div className="space-y-1.5">
                <Label>Gerencia <span className="text-red-500">*</span></Label>
                <Input value={form.gerencia} onChange={e => setForm(f => ({...f, gerencia: e.target.value}))} placeholder="Ej: Gerencia de Operaciones" data-testid="input-area-gerencia" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={e => setForm(f => ({...f, descripcion: e.target.value}))} placeholder="Descripción opcional" rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.recibe_notificaciones_entregas}
                onCheckedChange={v => setForm(f => ({...f, recibe_notificaciones_entregas: v}))}
                data-testid="switch-notif-entregas"
              />
              <Label className="text-sm">Recibe notificaciones de entregas</Label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={resetForm}><X size={14} className="mr-1" /> Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#4E7A79] hover:bg-[#3d6362] text-white" data-testid="btn-guardar-area">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                {editId ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="border border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[#9d8272]" />
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="tabla-areas">
              <thead>
                <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Nombre</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Gerencia</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A] hidden sm:table-cell">Descripción</th>
                  <th className="text-center p-3 font-medium text-[#5A5A5A]">Notif. Entregas</th>
                  <th className="text-center p-3 font-medium text-[#5A5A5A]">Estado</th>
                  <th className="text-center p-3 font-medium text-[#5A5A5A] w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {areas.map(a => (
                  <tr key={a.id} className="border-b border-[#E8E8E7] hover:bg-[#FAFAFA]">
                    <td className="p-3 font-medium text-[#2D2D2D]">{a.nombre}</td>
                    <td className="p-3 text-[#5A5A5A]">{a.gerencia}</td>
                    <td className="p-3 text-[#888] hidden sm:table-cell text-xs">{a.descripcion || '-'}</td>
                    <td className="p-3 text-center">
                      {a.recibe_notificaciones_entregas ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Sí</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-[#888]">No</Badge>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={a.activo ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-red-100 text-red-700 text-xs'}>
                        {a.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(a)} className="h-7 w-7 p-0" data-testid={`btn-edit-area-${a.nombre}`}>
                          <Edit2 size={13} />
                        </Button>
                        {a.activo && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`btn-delete-area-${a.nombre}`}>
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AreasFuncionales;
